import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './SampleCollectionPage.css';
import './SpecimenWorklistRedesign.css';
import './EnterpriseSpecimenWorklist.css';
import sampleCollectionService from '../services/sampleCollectionService';
import { useAuth } from '../context/AuthContext';
import CommonPagination from '../components/common/CommonPagination';
import PatientOrder360Modal from '../components/common/PatientOrder360Modal';
import ThermalBarcodePrintModal from '../components/common/ThermalBarcodePrintModal';
import PAGINATION_CONFIG from '../config/paginationConfig';
import useSampleStatusCounts from '../hooks/useSampleStatusCounts';
import { buildSampleSearchParam } from '../utils/sampleSearch';
import { localISODate } from '../utils/worklistDates';
import { sampleRowStatusClass, STATUS_FILTER_LABELS } from '../utils/sampleStatus';
import {
  SampleWorklistHeader,
  SampleSummaryCards,
  WorkflowStepper,
  WorklistState,
  DateRangeFields,
  WorklistContextBar,
} from '../components/sampleWorklist/SampleWorklistParts';
import SampleWorklistGrid from '../components/sampleWorklist/SampleWorklistGrid';

const COLLECTION_STEPS = [
  { label: 'Select Sample', hint: 'Tick rows or click Collect' },
  { label: 'Collect', hint: 'Confirm tubes drawn' },
  { label: 'Confirm Barcode', hint: 'Scan / verify SIN' },
  { label: 'Print Label', hint: 'Thermal tube sticker' },
  { label: 'Dispatch', hint: 'Send to Sample Receiving' },
];

const EMPTY_MESSAGES = {
  N: 'No samples pending collection',
  S: 'No collected samples awaiting receipt',
  Y: 'No received samples for these filters',
  R: 'No rejected samples for these filters',
  OUTSOURCE: 'No outsourced samples for these filters',
  ALL: 'No sample orders match these filters',
};

export const SampleCollectionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Search & Filter State
  const [searchField, setSearchField] = useState('ALL');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('N'); // Default to Pending for Phlebotomist workflow
  // Default the registration-date filter to today's local date (resolved at runtime)
  const [fromDate, setFromDate] = useState(() => localISODate());
  const [toDate, setToDate] = useState(() => localISODate());
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);

  // Redesign Operational UX States
  const [viewMode] = useState('GRID'); // grouped view toggle removed from the UI; grid is the worklist
  const [quickFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'ELIGIBLE'
  const [sortBy] = useState('NEW_FIRST'); // 'NEW_FIRST' | 'OLD_FIRST' | 'NAME' | 'URGENT'
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [selectedPatientModal, setSelectedPatientModal] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);
  const [lastCollectedCount, setLastCollectedCount] = useState(0);
  const [labelsPrinted, setLabelsPrinted] = useState(false);

  // Summary Metrics State
  const [summary, setSummary] = useState({
    pendingCount: 0,
    collectedTodayCount: 0,
    totalPatientsToday: 0,
  });

  // Multi-Selection State for Batch Collection
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());

  // Collection Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalItems, setModalItems] = useState([]);
  const [generalRemarks, setGeneralRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Barcode Label Print Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printItem, setPrintItem] = useState(null);

  // Rejection Modal State
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [customReasonText, setCustomReasonText] = useState('');
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  // Rejection Log State
  const [rejectionLog, setRejectionLog] = useState([]);
  const [rejLogLoading, setRejLogLoading] = useState(false);
  const [showRejectionLog] = useState(false);

  // Notification Toast
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  // Fetch Phlebotomy Metrics
  const fetchSummary = useCallback(async () => {
    try {
      const res = await sampleCollectionService.getSummary();
      if (res.success && res.data) {
        setSummary(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch phlebotomy summary metrics:', err);
    }
  }, []);

  // Filters shared by the worklist and the per-status counts (everything except status / paging)
  const filterParams = useMemo(() => {
    const params = buildSampleSearchParam(searchField, searchValue);
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return params;
  }, [searchField, searchValue, fromDate, toDate]);

  // Fetch Worklist Samples
  const fetchSamples = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sampleCollectionService.searchSamples({
        ...filterParams,
        status: statusFilter,
        pageNumber,
        pageSize,
      });
      if (res.success && res.data) {
        setSamples(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setLoadError(null);
      } else {
        setSamples([]);
        setTotalCount(0);
        setLoadError(res.message || 'The server did not return the sample worklist.');
      }
    } catch (err) {
      console.error('Failed to fetch phlebotomy worklist:', {
        status: err.response?.status,
        body: err.response?.data,
        error: err,
      });
      const status = err.response?.status;
      setLoadError(
        err.response?.data?.message ||
          (status ? `Request failed (HTTP ${status}).` : 'The LIS server could not be reached. Check the network connection.')
      );
      setSamples([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filterParams, statusFilter, pageNumber, pageSize]);

  // Per-status totals, same endpoint and filters as the list
  const { counts, loading: countsLoading } = useSampleStatusCounts(
    sampleCollectionService.searchSamples,
    filterParams,
    countsRefreshKey
  );

  const refreshAll = () => {
    fetchSummary();
    fetchSamples();
    setCountsRefreshKey((k) => k + 1);
  };

  // Reset to page 1 whenever filters change (matches Sample Receive), so a narrower
  // filter never lands on an empty page beyond its last page.
  useEffect(() => {
    setPageNumber(1);
  }, [searchField, searchValue, statusFilter, fromDate, toDate]);

  // Initial Load
  useEffect(() => {
    fetchSummary();
    fetchSamples();
  }, [fetchSummary, fetchSamples]);

  // Handle Search Submission
  const handleSearch = (e) => {
    e.preventDefault();
    setSelectedTestIds(new Set());
    fetchSamples();
  };

  // Quick Barcode Scan Handler
  const handleBarcodeScan = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchField('BARCODE');
      setSelectedTestIds(new Set());
      fetchSamples();
    }
  };

  // Derive patient groups from current page samples (Order / Visit level grouping)
  const patientGroups = useMemo(() => {
    if (!samples || samples.length === 0) return [];
    const map = new Map();
    samples.forEach((sample) => {
      const key = sample.ledgerTransactionNo || sample.patientId || String(sample.ledgerTransactionId || sample.testId);
      if (!map.has(key)) {
        map.set(key, {
          ...sample,
          groupKey: key,
          patientName: sample.patientName || 'Unknown Patient',
          patientId: sample.patientId || '',
          ledgerTransactionNo: sample.ledgerTransactionNo || '',
          ageGender: sample.ageGender || '',
          mobile: sample.mobile || '',
          priority: sample.priority || 'Routine',
          isUrgent: Boolean(sample.isUrgent || sample.priority === 'Urgent'),
          centreName: sample.centreName || '',
          registrationDate: sample.registrationDate,
          samples: [],
        });
      }
      map.get(key).samples.push(sample);
    });

    return Array.from(map.values()).map((grp) => {
      const total = grp.samples.length;
      const pending = grp.samples.filter((s) => s.isSampleCollected === 'N').length;
      const collected = grp.samples.filter((s) => s.isSampleCollected === 'S').length;
      const received = grp.samples.filter((s) => s.isSampleCollected === 'Y').length;
      const rejected = grp.samples.filter((s) => s.isSampleCollected === 'R').length;
      const eligible = grp.samples.filter((s) => s.isSampleCollected === 'N');

      let statusCode = 'pending';
      let statusLabel = '⏳ Pending Collection';

      if (collected === total && total > 0) {
        statusCode = 'collected';
        statusLabel = '🧪 Fully Collected';
      } else if (collected > 0 && pending > 0) {
        statusCode = 'partial-collected';
        statusLabel = `🧪 Partially Collected (${collected}/${total})`;
      } else if (received === total && total > 0) {
        statusCode = 'received';
        statusLabel = '🔬 Received in Lab';
      } else if (rejected === total && total > 0) {
        statusCode = 'rejected';
        statusLabel = '⛔ All Rejected';
      } else if (pending === total && total > 0) {
        statusCode = 'pending';
        statusLabel = '⏳ Pending Collection';
      }

      return {
        ...grp,
        pendingCount: pending,
        collectedCount: collected,
        receivedCount: received,
        rejectedCount: rejected,
        eligibleSamples: eligible,
        groupStatus: { code: statusCode, label: statusLabel },
      };
    });
  }, [samples]);

  // Count of eligible pending items currently selected
  const selectedEligibleCount = useMemo(() => {
    return samples.filter((s) => selectedTestIds.has(s.testId) && s.isSampleCollected === 'N').length;
  }, [samples, selectedTestIds]);

  // Container Cap Color & Styling Resolver
  const getContainerInfo = (containerName, containerColor) => {
    const cName = (containerName || '').toLowerCase();
    if (cName.includes('yellow') || cName.includes('sst') || cName.includes('gel')) {
      return { dotColor: '#eab308', bg: '#fefce8', border: '#fde047', label: containerName || 'Yellow Top (SST Gel Separator)' };
    }
    if (cName.includes('lavender') || cName.includes('purple') || cName.includes('edta')) {
      return { dotColor: '#a855f7', bg: '#faf5ff', border: '#e9d5ff', label: containerName || 'Lavender Top (K2/K3 EDTA)' };
    }
    if (cName.includes('red') || cName.includes('plain')) {
      return { dotColor: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: containerName || 'Red Top (Plain Tube)' };
    }
    if (cName.includes('blue') || cName.includes('citrate')) {
      return { dotColor: '#0ea5e9', bg: '#f0f9ff', border: '#bae6fd', label: containerName || 'Light Blue Top (Sodium Citrate)' };
    }
    if (cName.includes('green') || cName.includes('heparin')) {
      return { dotColor: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', label: containerName || 'Green Top (Lithium Heparin)' };
    }
    if (cName.includes('gray') || cName.includes('grey') || cName.includes('fluoride')) {
      return { dotColor: '#64748b', bg: '#f8fafc', border: '#cbd5e1', label: containerName || 'Gray Top (Sodium Fluoride)' };
    }
    if (cName.includes('cup') || cName.includes('urine') || cName.includes('sterile')) {
      return { dotColor: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', label: containerName || 'Sterile Universal Cup' };
    }
    const color = containerColor || '#6366f1';
    return { dotColor: color, bg: `${color}15`, border: `${color}40`, label: containerName || 'Standard Vacutainer' };
  };

  // Status cards: totals for the current filters (not just the visible page)
  const statusCards = [
    { key: 'ALL', label: 'Total', tone: 'neutral', count: counts.ALL },
    { key: 'N', label: 'Pending', tone: 'pending', count: counts.N, hint: 'Registered, awaiting collection' },
    { key: 'S', label: 'Collected', tone: 'collected', count: counts.S, hint: 'Collected, awaiting receipt in lab' },
    { key: 'Y', label: 'Received', tone: 'received', count: counts.Y },
    { key: 'OUTSOURCE', label: 'Outsourced', tone: 'outsource', count: counts.OUTSOURCE },
    { key: 'R', label: 'Rejected', tone: 'rejected', count: counts.R },
  ];

  // 0 Select → 1 Collect → 2 Confirm barcode (modal) → 3 Print label → 4 Dispatch
  const workflowStep = isModalOpen
    ? 2
    : selectedTestIds.size > 0
      ? 1
      : labelsPrinted
        ? 4
        : lastCollectedCount > 0
          ? 3
          : 0;

  // Sorted Patient Groups based on user preference
  const sortedPatientGroups = useMemo(() => {
    let list = [...patientGroups];
    if (sortBy === 'NEW_FIRST') {
      list.sort((a, b) => new Date(b.registrationDate || 0) - new Date(a.registrationDate || 0));
    } else if (sortBy === 'OLD_FIRST') {
      list.sort((a, b) => new Date(a.registrationDate || 0) - new Date(b.registrationDate || 0));
    } else if (sortBy === 'NAME') {
      list.sort((a, b) => (a.patientName || '').localeCompare(b.patientName || ''));
    } else if (sortBy === 'URGENT') {
      list.sort((a, b) => (b.isUrgent ? 1 : 0) - (a.isUrgent ? 1 : 0));
    }
    return list;
  }, [patientGroups, sortBy]);

  // Filter samples inside a patient group according to quickFilter pill
  const getVisibleSamplesForGroup = (group) => {
    if (quickFilter === 'PENDING') {
      return group.samples.filter((s) => s.isSampleCollected === 'N');
    }
    if (quickFilter === 'ELIGIBLE') {
      return group.eligibleSamples;
    }
    return group.samples;
  };

  // Toggle patient card expansion/collapse
  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };



  // Global select/deselect all eligible visible samples
  const handleSelectAllVisible = () => {
    const eligible = samples.filter((s) => s.isSampleCollected === 'N');
    if (selectedTestIds.size === eligible.length && eligible.length > 0) {
      setSelectedTestIds(new Set());
    } else {
      setSelectedTestIds(new Set(eligible.map((s) => s.testId)));
      showToast(`${eligible.length} eligible pending sample(s) selected.`, 'info');
    }
  };

  // Toggle selection for individual test item
  const handleToggleSelect = (testId, isCollected) => {
    if (isCollected !== 'N') return; // Cannot select already collected specimens
    setSelectedTestIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  // Toggle selection for all eligible specimens of a patient group
  const handleTogglePatientGroup = (group) => {
    if (!group.eligibleSamples || group.eligibleSamples.length === 0) return;
    const allSelected = group.eligibleSamples.every((s) => selectedTestIds.has(s.testId));
    setSelectedTestIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        group.eligibleSamples.forEach((s) => next.delete(s.testId));
      } else {
        group.eligibleSamples.forEach((s) => next.add(s.testId));
      }
      return next;
    });
  };

  // Toggle select all pending in current view
  const handleSelectAllPending = () => {
    const pendingItems = samples.filter((s) => s.isSampleCollected === 'N');
    if (selectedTestIds.size === pendingItems.length && pendingItems.length > 0) {
      setSelectedTestIds(new Set());
    } else {
      setSelectedTestIds(new Set(pendingItems.map((s) => s.testId)));
      showToast(`${pendingItems.length} eligible pending specimen(s) selected.`, 'info');
    }
  };

  // Clear all selections
  const handleClearSelection = () => {
    setSelectedTestIds(new Set());
  };

  // Open Collection Drawer / Modal for single item
  const handleOpenSingleCollection = (sample) => {
    if (sample.isSampleCollected !== 'N') {
      showToast(
        `Specimen for ${sample.itemName} is already marked as ${sample.status}.`,
        'warning'
      );
      return;
    }
    setModalItems([
      {
        testId: sample.testId,
        itemName: sample.itemName,
        itemCode: sample.itemCode,
        patientName: sample.patientName,
        patientId: sample.patientId,
        ledgerTransactionNo: sample.ledgerTransactionNo,
        departmentName: sample.departmentName,
        sampleTypeName: sample.sampleTypeName,
        containerName: sample.containerName,
        containerColor: sample.containerColor,
        barcodeNo: sample.barcodeNo || '',
        remarks: '',
        isOutsource: Boolean(sample.isOutsource || sample.processingMode === 'OUTSOURCE'),
        referenceLabName: sample.referenceLabName,
      },
    ]);
    setGeneralRemarks('');
    setIsModalOpen(true);
  };

  // Open Collection Confirmation Modal for all eligible specimens of a patient group
  const handleOpenPatientBulkCollection = (group) => {
    if (!group.eligibleSamples || group.eligibleSamples.length === 0) {
      showToast('No pending specimens eligible for collection for this patient.', 'warning');
      return;
    }
    const items = group.eligibleSamples.map((s) => ({
      testId: s.testId,
      itemName: s.itemName,
      itemCode: s.itemCode,
      patientName: s.patientName,
      patientId: s.patientId,
      ledgerTransactionNo: s.ledgerTransactionNo,
      departmentName: s.departmentName,
      sampleTypeName: s.sampleTypeName,
      containerName: s.containerName,
      containerColor: s.containerColor,
      barcodeNo: s.barcodeNo || '',
      remarks: '',
      isOutsource: Boolean(s.isOutsource || s.processingMode === 'OUTSOURCE'),
      referenceLabName: s.referenceLabName,
    }));
    setModalItems(items);
    setGeneralRemarks('');
    setIsModalOpen(true);
  };

  // Open Collection Drawer / Modal for batch selected items
  const handleOpenBatchCollection = () => {
    if (selectedTestIds.size === 0) return;
    const selectedItems = samples
      .filter((s) => selectedTestIds.has(s.testId) && s.isSampleCollected === 'N')
      .map((s) => ({
        testId: s.testId,
        itemName: s.itemName,
        itemCode: s.itemCode,
        patientName: s.patientName,
        patientId: s.patientId,
        ledgerTransactionNo: s.ledgerTransactionNo,
        departmentName: s.departmentName,
        sampleTypeName: s.sampleTypeName,
        containerName: s.containerName,
        containerColor: s.containerColor,
        barcodeNo: s.barcodeNo || '',
        remarks: '',
        isOutsource: Boolean(s.isOutsource || s.processingMode === 'OUTSOURCE'),
        referenceLabName: s.referenceLabName,
      }));

    if (selectedItems.length === 0) {
      showToast('None of the selected specimens are currently eligible for collection.', 'warning');
      return;
    }

    setModalItems(selectedItems);
    setGeneralRemarks('');
    setIsModalOpen(true);
  };

  // Barcode input change inside modal
  const handleModalBarcodeChange = (index, value) => {
    setModalItems((prev) => {
      const next = [...prev];
      next[index].barcodeNo = value;
      return next;
    });
  };

  // Remarks change inside modal
  const handleModalRemarksChange = (index, value) => {
    setModalItems((prev) => {
      const next = [...prev];
      next[index].remarks = value;
      return next;
    });
  };

  // Confirm Specimen Collection API Call with atomic/partial failure feedback
  const handleConfirmCollection = async () => {
    setSubmitting(true);
    try {
      const payload = {
        items: modalItems.map((item) => ({
          testId: item.testId,
          barcodeNo: item.barcodeNo?.trim() || undefined,
          remarks: item.remarks?.trim() || generalRemarks.trim() || undefined,
        })),
      };

      const res = await sampleCollectionService.collectSamples(payload);
      if (res.success) {
        const collectedIds = res.data?.collectedTestIds || modalItems.map((m) => m.testId);
        const failedCount = res.data?.failedCount || 0;
        const msg = res.message || `Successfully collected ${collectedIds.length} specimen(s).`;

        showToast(msg, failedCount > 0 ? 'warning' : 'success');
        setIsModalOpen(false);

        // Deselect collected test IDs
        setSelectedTestIds((prev) => {
          const next = new Set(prev);
          collectedIds.forEach((id) => next.delete(id));
          return next;
        });

        setLastCollectedCount(collectedIds.length);
        setLabelsPrinted(false);
        refreshAll();
      } else {
        showToast(res.message || 'Collection failed.', 'error');
      }
    } catch (err) {
      console.error('Sample collection error:', err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Failed to record specimen collection.';
      showToast(errMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Barcode Label Print Modal
  const handleOpenPrintBarcode = (sample) => {
    setPrintItem(sample);
    setIsPrintModalOpen(true);
  };

  // Patient visit & audit details (Log)
  const openPatientLog = (sample) => {
    const foundGroup = patientGroups.find((g) => g.patientId === sample.patientId);
    setSelectedPatientModal(foundGroup || { patientName: sample.patientName, samples: [sample], ...sample });
  };

  // Fixed action slots per row. Availability uses the existing rules unchanged
  // (Collect: N · Label: S/Y · Reject: N/S · Log: always); unavailable slots are frozen, not hidden.
  const getCollectionActions = (sample) => {
    const st = sample.isSampleCollected;
    const collectedOrReceived = st === 'S' || st === 'Y';
    return [
      {
        key: 'collect', label: 'Collect', icon: 'collect', tone: 'primary',
        state: st === 'N' ? 'ready' : collectedOrReceived ? 'done' : 'na',
        title: st === 'N' ? 'Collect specimen now' : collectedOrReceived ? 'Specimen already collected' : 'Rejected specimen cannot be collected',
        onClick: () => handleOpenSingleCollection(sample),
      },
      {
        key: 'label', label: 'Label', icon: 'label', tone: 'neutral',
        state: collectedOrReceived ? 'ready' : 'na',
        title: collectedOrReceived ? 'Print tube label' : 'Label is available after collection',
        onClick: () => handleOpenPrintBarcode(sample),
      },
      {
        key: 'reject', label: 'Reject', icon: 'reject', tone: 'danger',
        state: st === 'N' || st === 'S' ? 'ready' : st === 'R' ? 'done' : 'na',
        title: st === 'R' ? 'Specimen already rejected' : st === 'Y' ? 'Received specimens are rejected from Sample Receive' : 'Reject specimen',
        onClick: () => handleOpenReject(sample),
      },
      {
        key: 'log', label: 'Log', icon: 'log', tone: 'neutral', size: 'sm', state: 'ready',
        title: 'View patient visit & audit details',
        onClick: () => openPatientLog(sample),
      },
    ];
  };

  // Fetch Rejection Reasons (shared master)
  const fetchRejectionReasons = useCallback(async () => {
    try {
      const res = await sampleCollectionService.getRejectionReasons();
      if (res.success && Array.isArray(res.data)) {
        setRejectionReasons(res.data);
        if (res.data.length > 0) setSelectedReasonId(res.data[0].rejectionId.toString());
      }
    } catch (err) {
      console.error('Failed to load rejection reasons:', err);
    }
  }, []);

  // Fetch Today's Rejection Log
  const fetchRejectionLog = useCallback(async () => {
    setRejLogLoading(true);
    try {
      const res = await sampleCollectionService.getRejectionLog();
      if (res.success && Array.isArray(res.data)) {
        setRejectionLog(res.data);
      } else {
        setRejectionLog([]);
      }
    } catch (err) {
      console.error('Failed to load rejection log:', err);
      setRejectionLog([]);
    } finally {
      setRejLogLoading(false);
    }
  }, []);

  // Load rejection reasons on mount
  useEffect(() => {
    fetchRejectionReasons();
  }, [fetchRejectionReasons]);

  // Open Rejection Modal
  const handleOpenReject = (sample) => {
    if (sample.isSampleCollected === 'R') {
      showToast('Specimen is already marked as Rejected.', 'info');
      return;
    }
    if (sample.isSampleCollected === 'Y') {
      showToast('Cannot reject: specimen is already received in laboratory.', 'warning');
      return;
    }
    setRejectTarget(sample);
    setCustomReasonText('');
    setRejectRemarks('');
    if (rejectionReasons.length > 0) setSelectedReasonId(rejectionReasons[0].rejectionId.toString());
    setIsRejectModalOpen(true);
  };

  // Confirm Rejection
  const handleConfirmReject = async () => {
    if (!rejectTarget) return;

    const selectedObj = rejectionReasons.find(
      (r) => r.rejectionId.toString() === selectedReasonId
    );
    const isCustom = selectedReasonId === 'CUSTOM';
    const reasonText = isCustom
      ? customReasonText.trim()
      : selectedObj?.rejectionReason || '';

    if (!reasonText) {
      showToast('Please select or enter a rejection reason.', 'warning');
      return;
    }

    setSubmittingReject(true);
    try {
      const payload = {
        items: [{
          testId: rejectTarget.testId,
          rejectionReasonId: isCustom ? undefined : selectedObj?.rejectionId,
          rejectionReason: reasonText,
          remarks: rejectRemarks.trim() || undefined,
        }],
      };
      const res = await sampleCollectionService.rejectSamples(payload);
      if (res.success) {
        showToast(`Specimen "${rejectTarget.itemName}" rejected at collection. Reason: ${reasonText}`, 'success');
        setIsRejectModalOpen(false);
        setRejectTarget(null);
        refreshAll();
        if (showRejectionLog) fetchRejectionLog();
      } else {
        showToast(res.message || 'Rejection failed.', 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to reject specimen.';
      showToast(msg, 'error');
    } finally {
      setSubmittingReject(false);
    }
  };

  return (
    <div className="elw-root">
      {/* Toast Notification */}
      {toast && (
        <div className={`sw-toast sw-toast-${toast.type}`}>
          <span className="sw-toast-icon">
            {toast.type === 'success' ? '✓' : toast.type === 'warning' ? '⚠' : '✕'}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* ── 1. Header: title, centre, user, time ── */}
      <SampleWorklistHeader
        title="Sample Collection"
        centreName={user?.centreName}
        userName={user?.employeeName || user?.username}
      >
        {summary.totalPatientsToday > 0 && (
          <span className="swl-metric" title="Distinct registrations created today in this centre">
            Registered today: <strong>{summary.totalPatientsToday}</strong>
          </span>
        )}
      </SampleWorklistHeader>

      {/* ── 2. Worklist summary (status cards = status filter) ── */}
      <SampleSummaryCards
        cards={statusCards}
        active={statusFilter}
        loading={countsLoading}
        onSelect={(key) => {
          setStatusFilter(key);
          setPageNumber(1);
          setSelectedTestIds(new Set());
        }}
      />

      <WorklistContextBar fromDate={fromDate} toDate={toDate} statusLabel={STATUS_FILTER_LABELS[statusFilter]} />

      {/* ── 3. Filter bar ── */}
      <form className="swl-filterbar" onSubmit={handleSearch} aria-label="Worklist filters">
        <DateRangeFields
          idPrefix="sc"
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
        <div className="swl-filter-field swl-filter-search">
          <label htmlFor="sc-search">Search patient / Lab No / UHID / barcode</label>
          <div className="swl-search-row">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              title="Identifier to search"
              aria-label="Identifier to search"
            >
              <option value="ALL">All Identifiers</option>
              <option value="UHID">UHID</option>
              <option value="LABNO">Lab No</option>
              <option value="PATIENT">Patient Name</option>
              <option value="BARCODE">Barcode</option>
              <option value="SAMPLENO">Sample / Test No</option>
              <option value="INVESTIGATION">Test Name</option>
            </select>
            <input
              id="sc-search"
              type="text"
              placeholder="Type or scan barcode, then Enter"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleBarcodeScan}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="swl-filter-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            🔍 Search
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setSearchValue('');
              setSearchField('ALL');
              setFromDate('');
              setToDate('');
              setStatusFilter('N');
              setPageNumber(1);
              setSelectedTestIds(new Set());
            }}
          >
            Reset
          </button>
        </div>
      </form>

      {/* ── 4. Workflow stepper ── */}
      <WorkflowStepper steps={COLLECTION_STEPS} activeIndex={workflowStep} />

      {/* ── 4. Sticky Floating Bulk Action Bar ── */}
      {selectedTestIds.size > 0 && (
        <section className="elw-bulk-bar" aria-label="Bulk Selection Controls">
          <div className="elw-bulk-left">
            <span>☑ Selected:</span>
            <span className="elw-bulk-pill">
              {selectedTestIds.size} specimens ({selectedEligibleCount} eligible for collection)
            </span>
          </div>

          <div className="elw-bulk-right">
            <button
              type="button"
              className="elw-btn-bulk-collect"
              onClick={handleOpenBatchCollection}
              disabled={selectedEligibleCount === 0}
              title={
                selectedEligibleCount === 0
                  ? 'No selected items are eligible for collection'
                  : `Collect all ${selectedEligibleCount} selected eligible specimen(s)`
              }
            >
              🩸 Collect Selected ({selectedEligibleCount})
            </button>

            <button
              type="button"
              className="elw-btn-bulk-print"
              onClick={() => {
                const firstWithBarcode = samples.find((s) => selectedTestIds.has(s.testId) && s.barcodeNo);
                if (firstWithBarcode) {
                  handleOpenPrintBarcode(firstWithBarcode);
                } else {
                  showToast('No barcodes allocated for selected samples', 'info');
                }
              }}
              title="Print Barcode Labels for Selected"
            >
              🖨️ Print Labels
            </button>

            <button
              type="button"
              className="elw-btn-bulk-clear"
              onClick={handleClearSelection}
              title="Clear current selection"
            >
              ✕ Clear
            </button>
          </div>
        </section>
      )}

      {/* ── 5. Main Worklist Presentation Area (Enterprise Table-First Architecture) ── */}
      <main className="elw-main">
        {loading ? (
          <div className="elw-table-wrapper">
            <table className="elw-table">
              <thead>
                <tr>
                  <th className="elw-th-check"><span className="elw-th-shimmer" style={{ width: '18px', height: '18px' }} /></th>
                  <th className="elw-th-sno">#</th>
                  <th className="elw-th-test">Investigation / Test</th>
                  <th className="elw-th-dept">Department</th>
                  <th className="elw-th-specimen">Specimen & Container</th>
                  <th className="elw-th-barcode">Barcode / SIN</th>
                  <th className="elw-th-status">Status</th>
                  <th className="elw-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <tr key={n} className="elw-row-skeleton">
                    <td colSpan="8">
                      <div className="elw-skeleton-line" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : loadError ? (
          <WorklistState
            type="error"
            title="Unable to load sample worklist"
            message={loadError}
            actionLabel="Retry"
            onAction={refreshAll}
          />
        ) : samples.length === 0 ? (
          <WorklistState
            title={EMPTY_MESSAGES[statusFilter] || EMPTY_MESSAGES.ALL}
            message={
              searchValue.trim() || fromDate || toDate
                ? 'No orders match the current search or date filters.'
                : statusFilter === 'N'
                  ? 'All registered samples for this centre have been collected.'
                  : undefined
            }
            actionLabel={searchValue.trim() || fromDate || toDate || statusFilter !== 'ALL' ? 'Show all samples' : undefined}
            onAction={() => {
              setSearchValue('');
              setSearchField('ALL');
              setFromDate('');
              setToDate('');
              setStatusFilter('ALL');
              setPageNumber(1);
            }}
          />
        ) : viewMode === 'GRID' ? (
          <SampleWorklistGrid
            samples={samples}
            selectedIds={selectedTestIds}
            isSelectable={(s) => s.isSampleCollected === 'N'}
            onToggleSelect={(s) => handleToggleSelect(s.testId, s.isSampleCollected)}
            allSelected={
              samples.some((s) => s.isSampleCollected === 'N') &&
              selectedTestIds.size === samples.filter((s) => s.isSampleCollected === 'N').length
            }
            onToggleAll={handleSelectAllVisible}
            selectAllTitle="Select all pending specimens on this page"
            getContainerInfo={getContainerInfo}
            onPrintBarcode={handleOpenPrintBarcode}
            getActions={getCollectionActions}
            actionSlots={{ md: 3, sm: 1 }}
          />
        ) : (
          /* ── High-Density Single Worklist Table (Patient-Wise Embedded Groups) ── */
          <div className="elw-table-wrapper">
            <table className="elw-table">
              <thead>
                <tr>
                  <th className="elw-th-check">
                    <input
                      type="checkbox"
                      checked={
                        samples.filter((s) => s.isSampleCollected === 'N').length > 0 &&
                        selectedTestIds.size === samples.filter((s) => s.isSampleCollected === 'N').length
                      }
                      onChange={handleSelectAllVisible}
                      title="Select all eligible specimens on this page"
                      aria-label="Select all eligible specimens"
                    />
                  </th>
                  <th className="elw-th-sno">#</th>
                  <th className="elw-th-test">Investigation / Test</th>
                  <th className="elw-th-dept">Department</th>
                  <th className="elw-th-specimen">Specimen & Container</th>
                  <th className="elw-th-barcode">Barcode / SIN</th>
                  <th className="elw-th-status">Status</th>
                  <th className="elw-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedPatientGroups.map((group, groupIdx) => {
                  const isGroupAllEligibleSelected =
                    group.eligibleSamples.length > 0 &&
                    group.eligibleSamples.every((s) => selectedTestIds.has(s.testId));
                  const isCollapsed = collapsedGroups.has(group.groupKey);
                  const visibleSamples = getVisibleSamplesForGroup(group);

                  return (
                    <React.Fragment key={group.groupKey}>
                      {/* Compact Patient Identity Strip */}
                      <tr className="elw-patient-group-row">
                        <td colSpan="8" className="elw-patient-strip-td">
                          <div className={`elw-patient-strip ${group.priority === 'Urgent' || group.isUrgent ? 'is-urgent' : ''}`}>
                            <div className="elw-patient-strip-left">
                              <label className="elw-pcheck" title="Select all eligible specimens for this patient">
                                <input
                                  type="checkbox"
                                  checked={isGroupAllEligibleSelected}
                                  disabled={group.eligibleSamples.length === 0}
                                  onChange={() => handleTogglePatientGroup(group)}
                                  aria-label={`Select all eligible for ${group.patientName}`}
                                />
                              </label>

                              <span className="elw-pindex font-mono">{groupIdx + 1}</span>

                              <span className="elw-pname" title={group.patientName}>{group.patientName}</span>

                              {(group.priority === 'Urgent' || group.isUrgent) && (
                                <span className="elw-purgent">🚨 URGENT</span>
                              )}

                              <span className="elw-pdemog">{group.ageGender || '—'}</span>

                              {group.mobile && (
                                <span className="elw-pchip" title="Patient Mobile">📞 {group.mobile}</span>
                              )}
                              <span className="elw-pchip font-mono" title="UHID / Patient ID">
                                <span className="elw-pchip-lbl">UHID:</span>{group.patientId}
                              </span>
                              <span className="elw-pchip font-mono" title="Lab Transaction No">
                                <span className="elw-pchip-lbl">LAB:</span>{group.ledgerTransactionNo}
                              </span>
                            </div>

                            <div className="elw-patient-strip-right">
                              <span className="elw-ptest-count">
                                {group.samples.length} Specimen{group.samples.length !== 1 ? 's' : ''}
                              </span>

                              <button
                                type="button"
                                className="elw-btn-pcollect"
                                onClick={() => handleOpenPatientBulkCollection(group)}
                                disabled={group.eligibleSamples.length === 0}
                                title={
                                  group.eligibleSamples.length === 0
                                    ? 'No pending specimens eligible for collection'
                                    : `Collect all ${group.eligibleSamples.length} eligible specimens for ${group.patientName}`
                                }
                              >
                                🩸 Collect All ({group.eligibleSamples.length})
                              </button>

                              <button
                                type="button"
                                className="elw-btn-plog"
                                onClick={() => setSelectedPatientModal(group)}
                                title="View Patient Visit & Complete Audit Details"
                              >
                                📜 360° Log
                              </button>

                              <button
                                type="button"
                                className="elw-btn-collapse"
                                aria-label={isCollapsed ? 'Expand patient tests' : 'Collapse patient tests'}
                                onClick={() => toggleGroupCollapse(group.groupKey)}
                                title={isCollapsed ? 'Expand tests' : 'Collapse tests'}
                              >
                                {isCollapsed ? '▼' : '▲'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Specimen Test Rows */}
                      {!isCollapsed &&
                        visibleSamples.map((sample, sIdx) => {
                          const isPending = sample.isSampleCollected === 'N';
                          const isCollected = sample.isSampleCollected === 'S';
                          const isReceived = sample.isSampleCollected === 'Y';
                          const isRejected = sample.isSampleCollected === 'R';
                          const isOutsource = Boolean(
                            sample.isOutsource || sample.processingMode === 'OUTSOURCE' || sample.referenceLabName
                          );
                          const isSelected = selectedTestIds.has(sample.testId);
                          const tubeInfo = getContainerInfo(sample.containerName, sample.containerColor);

                          return (
                            <tr key={sample.testId} className={`elw-row ${sampleRowStatusClass(sample)} ${isSelected ? 'is-selected' : ''}`}>
                              {/* Selection */}
                              <td className="elw-td-check">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={!isPending}
                                  onChange={() => handleToggleSelect(sample.testId, sample.isSampleCollected)}
                                  aria-label={`Select ${sample.itemName}`}
                                />
                              </td>

                              {/* S.No */}
                              <td className="elw-td-sno font-mono">{sIdx + 1}</td>

                              {/* Investigation / Test */}
                              <td className="elw-td-test">
                                <div className="elw-test-title-col">
                                  <span className="elw-test-name">{sample.itemName}</span>
                                  {isOutsource && (
                                    <span className="elw-outsource-tag">
                                      🌐 OUTSOURCE • {sample.referenceLabName || 'Reference Lab'}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Department */}
                              <td className="elw-td-dept">
                                <span className="elw-dept-badge">{sample.departmentName || 'General'}</span>
                              </td>

                              {/* Specimen & Container */}
                              <td className="elw-td-specimen">
                                <div className="elw-specimen-cell">
                                  <span
                                    className="elw-swatch-box"
                                    style={{
                                      backgroundColor: tubeInfo.dotColor,
                                      borderColor: tubeInfo.border,
                                    }}
                                    title={`${tubeInfo.label}${sample.containerName ? ` (${sample.containerName})` : ''}`}
                                    aria-label={tubeInfo.label}
                                  />
                                  <span className="elw-specimen-name">{sample.sampleTypeName || 'Serum'}</span>
                                </div>
                              </td>

                              {/* Barcode / SIN */}
                              <td className="elw-td-barcode">
                                <div className="elw-barcode-cell">
                                  {sample.barcodeNo ? (
                                    <>
                                      <span className="elw-barcode-pill" title={sample.barcodeNo}>
                                        🏷️ {sample.barcodeNo}
                                      </span>
                                      <button
                                        type="button"
                                        className="elw-btn-barcode-icon"
                                        title="Print Thermal Barcode Label"
                                        onClick={() => handleOpenPrintBarcode(sample)}
                                      >
                                        🖨️
                                      </button>
                                    </>
                                  ) : (
                                    <span className="elw-na-text">—</span>
                                  )}
                                </div>
                              </td>

                              {/* Status */}
                              <td className="elw-td-status">
                                <span
                                  className={`elw-status-pill status-${isReceived ? 'received' : isCollected ? 'collected' : isRejected ? 'rejected' : 'pending'}`}
                                >
                                  {isReceived
                                    ? 'RECEIVED'
                                    : isCollected
                                    ? 'COLLECTED'
                                    : isRejected
                                    ? 'REJECTED'
                                    : 'PENDING'}
                                </span>
                              </td>

                              {/* Actions */}
                              <td className="elw-td-actions">
                                <div className="elw-actions-flex">
                                  {isPending && (
                                    <button
                                      type="button"
                                      className="elw-btn-action-collect"
                                      onClick={() => handleOpenSingleCollection(sample)}
                                      title="Collect specimen now"
                                    >
                                      🩸 Collect
                                    </button>
                                  )}

                                  {(isPending || isCollected) && !isRejected && !isReceived && (
                                    <button
                                      type="button"
                                      className="elw-btn-action-reject"
                                      onClick={() => handleOpenReject(sample)}
                                      title="Reject specimen"
                                    >
                                      ✕ Reject
                                    </button>
                                  )}

                                  {(isCollected || isReceived) && (
                                    <button
                                      type="button"
                                      className="elw-btn-action-label"
                                      onClick={() => handleOpenPrintBarcode(sample)}
                                      title="Print Tube Label"
                                    >
                                      🏷️ Label
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    className="elw-btn-action-log"
                                    onClick={() => setSelectedPatientModal(group)}
                                    title="View Patient Visit & Audit Details"
                                  >
                                    📜 Log
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Centralized Pagination Bar */}
        <div className="elw-pagination-bar">
          <CommonPagination
            page={pageNumber}
            pageSize={pageSize}
            totalRecords={totalCount}
            onPageChange={(p) => setPageNumber(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPageNumber(1);
            }}
            loading={loading}
          />
        </div>

        {/* Collection-Stage Rejection Log Collapsible Section */}
        {showRejectionLog && (
          <section className="elw-rejection-section">
            <div className="elw-rejection-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⛔</span>
                <span className="font-semibold" style={{ color: '#991b1b', fontSize: '0.85rem' }}>
                  Collection-Stage Rejection Audit Log
                </span>
                {rejectionLog.length > 0 && (
                  <span className="elw-count-badge" style={{ background: '#fecaca', color: '#991b1b' }}>
                    {rejectionLog.length}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="elw-btn-outline-sm"
                onClick={fetchRejectionLog}
                disabled={rejLogLoading}
              >
                🔄 Refresh Log
              </button>
            </div>

            <div style={{ padding: '0' }}>
              {rejLogLoading ? (
                <div style={{ padding: '16px', textAlign: 'center', fontSize: '0.82rem', color: '#64748b' }}>
                  Loading rejection log...
                </div>
              ) : rejectionLog.length === 0 ? (
                <div style={{ padding: '16px', color: '#16a34a', textAlign: 'center', fontSize: '0.82rem' }}>
                  ✓ No specimens rejected at collection stage today.
                </div>
              ) : (
                <div className="elw-table-wrapper" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
                  <table className="elw-table">
                    <thead>
                      <tr>
                        <th className="elw-th-sno">#</th>
                        <th>Lab No</th>
                        <th>Patient</th>
                        <th>Investigation</th>
                        <th>Reason</th>
                        <th>Remarks</th>
                        <th>Rejected By</th>
                        <th>Rejected At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejectionLog.map((r, idx) => (
                        <tr key={r.testId} className="elw-row">
                          <td className="elw-td-sno font-mono">{idx + 1}</td>
                          <td className="font-mono" style={{ fontWeight: 600 }}>{r.ledgerTransactionNo}</td>
                          <td>
                            <strong>{r.patientName}</strong>
                            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>UHID: {r.patientId}</div>
                          </td>
                          <td>{r.itemName}</td>
                          <td>
                            <span className="elw-status-badge status-r">{r.rejectionReason}</span>
                          </td>
                          <td>{r.remarks || '—'}</td>
                          <td>{r.rejectedBy || '—'}</td>
                          <td style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {r.rejectedAt
                              ? new Date(r.rejectedAt).toLocaleString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Collection Drawer / Modal */}
      {isModalOpen && (
        <div className="phleb-modal-overlay">
          <div className="phleb-modal-card">
            <div className="phleb-modal-header">
              <div>
                <h2 className="phleb-modal-title">Specimen Collection Confirmation</h2>
                <p className="phleb-modal-subtitle">
                  Confirm specimen draw, verify/scan vacutainer barcodes, and record phlebotomy notes.
                </p>
              </div>
              <button
                type="button"
                className="phleb-modal-close"
                onClick={() => setIsModalOpen(false)}
                disabled={submitting}
              >
                ✕
              </button>
            </div>

            <div className="phleb-modal-body">
              {/* Phlebotomist Bar */}
              <div className="phleb-collector-bar">
                <div className="phleb-cb-item">
                  <span className="phleb-cb-label">Phlebotomist / Collector:</span>
                  <span className="phleb-cb-value">
                    👤 {user?.employeeName || user?.username || 'Current User'}
                  </span>
                </div>
                <div className="phleb-cb-item">
                  <span className="phleb-cb-label">Collection Timestamp:</span>
                  <span className="phleb-cb-value">
                    🕒 {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} (Current Time)
                  </span>
                </div>
              </div>

              {/* List of Tubes / Tests */}
              <div className="phleb-tubes-list">
                <h3 className="phleb-tubes-title">Specimen Tubes to Collect ({modalItems.length})</h3>
                {modalItems.map((item, index) => (
                  <div key={item.testId} className={`phleb-tube-card ${item.isOutsource || item.processingMode === 'OUTSOURCE' ? 'phleb-tube-card-outsource' : ''}`}>
                    <div className="phleb-tube-info">
                      <div className="phleb-tube-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <strong>{item.itemName}</strong>
                          {(item.isOutsource || item.processingMode === 'OUTSOURCE' || item.referenceLabName) && (
                            <span className="badge-outsource-phleb" title={item.referenceLabName ? `Partner Lab: ${item.referenceLabName}` : 'Outsource Investigation'}>
                              🌐 OUTSOURCE{item.referenceLabName ? ` • ${item.referenceLabName}` : ''}
                            </span>
                          )}
                        </div>
                        <span className="phleb-tube-dept">({item.departmentName})</span>
                      </div>
                      <div className="phleb-tube-demog">
                        <span>Patient: <strong>{item.patientName}</strong></span> • 
                        <span>LabNo: <strong>{item.ledgerTransactionNo}</strong></span> • 
                        <span>UHID: <strong>{item.patientId}</strong></span>
                      </div>
                      <div className="phleb-tube-specimen-props">
                        <span className="phleb-badge-sample">Specimen: {item.sampleTypeName}</span>
                        <span
                          className="phleb-badge-container"
                          style={{
                            borderColor: item.containerColor || '#6366f1',
                            color: item.containerColor || '#6366f1',
                          }}
                        >
                          ● {item.containerName}
                        </span>
                      </div>
                    </div>

                    <div className="phleb-tube-inputs">
                      <div className="phleb-fg">
                        <label>Barcode / SIN (Scannable)</label>
                        <input
                          type="text"
                          value={item.barcodeNo}
                          placeholder="Scan tube barcode..."
                          onChange={(e) => handleModalBarcodeChange(index, e.target.value)}
                          className="phleb-input font-mono"
                        />
                      </div>
                      <div className="phleb-fg">
                        <label>Remarks / Condition (Optional)</label>
                        <input
                          type="text"
                          value={item.remarks}
                          placeholder="e.g. Non-fasting, Left arm, 3ml..."
                          onChange={(e) => handleModalRemarksChange(index, e.target.value)}
                          className="phleb-input"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* General Remarks */}
              <div className="phleb-form-group">
                <label htmlFor="generalRemarks">Batch Remarks / Clinical Note (Optional)</label>
                <textarea
                  id="generalRemarks"
                  rows="2"
                  placeholder="Additional notes on specimen condition, patient state, or draw issues..."
                  value={generalRemarks}
                  onChange={(e) => setGeneralRemarks(e.target.value)}
                  className="phleb-input"
                ></textarea>
              </div>
            </div>

            <div className="phleb-modal-footer">
              <button
                type="button"
                className="phleb-btn phleb-btn-secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="phleb-btn phleb-btn-primary phleb-btn-collect"
                onClick={handleConfirmCollection}
                disabled={submitting}
              >
                {submitting ? 'Recording Collection...' : '✓ Confirm Specimen Collection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Thermal Label Sticker Print Modal */}
      {isPrintModalOpen && printItem && (
        <div className="phleb-modal-overlay">
          <div className="phleb-modal-card phleb-modal-print">
            <div className="phleb-modal-header">
              <div>
                <h2 className="phleb-modal-title">Specimen Tube Barcode Label</h2>
                <p className="phleb-modal-subtitle">Thermal printer sticker preview (50mm x 25mm standard)</p>
              </div>
              <button
                type="button"
                className="phleb-modal-close"
                onClick={() => setIsPrintModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="phleb-modal-body phleb-print-body">
              {/* Thermal Label Simulation */}
              <div className="phleb-label-sticker" id="printable-thermal-label">
                <div className="phleb-label-top">
                  <span className="phleb-lbl-hosp">AURO DIAGNOSTICS</span>
                  <span className="phleb-lbl-date">
                    {new Date(printItem.registrationDate).toLocaleDateString('en-IN')}
                  </span>
                </div>
                <div className="phleb-label-pname">
                  {printItem.patientName} ({printItem.ageGender})
                </div>
                <div className="phleb-label-ids">
                  <span>UHID: {printItem.patientId}</span>
                  <span>LAB: {printItem.ledgerTransactionNo}</span>
                </div>

                {/* Simulated High-Res Barcode SVG */}
                <div className="phleb-label-barcode-art">
                  <svg className="phleb-barcode-svg" viewBox="0 0 160 40">
                    <rect x="5" y="0" width="2" height="35" fill="#000" />
                    <rect x="9" y="0" width="4" height="35" fill="#000" />
                    <rect x="16" y="0" width="1" height="35" fill="#000" />
                    <rect x="20" y="0" width="3" height="35" fill="#000" />
                    <rect x="26" y="0" width="2" height="35" fill="#000" />
                    <rect x="31" y="0" width="5" height="35" fill="#000" />
                    <rect x="39" y="0" width="2" height="35" fill="#000" />
                    <rect x="44" y="0" width="1" height="35" fill="#000" />
                    <rect x="48" y="0" width="4" height="35" fill="#000" />
                    <rect x="55" y="0" width="2" height="35" fill="#000" />
                    <rect x="60" y="0" width="3" height="35" fill="#000" />
                    <rect x="66" y="0" width="1" height="35" fill="#000" />
                    <rect x="70" y="0" width="4" height="35" fill="#000" />
                    <rect x="77" y="0" width="2" height="35" fill="#000" />
                    <rect x="82" y="0" width="3" height="35" fill="#000" />
                    <rect x="88" y="0" width="1" height="35" fill="#000" />
                    <rect x="92" y="0" width="4" height="35" fill="#000" />
                    <rect x="99" y="0" width="2" height="35" fill="#000" />
                    <rect x="104" y="0" width="3" height="35" fill="#000" />
                    <rect x="110" y="0" width="5" height="35" fill="#000" />
                    <rect x="118" y="0" width="2" height="35" fill="#000" />
                    <rect x="123" y="0" width="1" height="35" fill="#000" />
                    <rect x="127" y="0" width="4" height="35" fill="#000" />
                    <rect x="134" y="0" width="2" height="35" fill="#000" />
                    <rect x="139" y="0" width="4" height="35" fill="#000" />
                    <rect x="146" y="0" width="2" height="35" fill="#000" />
                    <rect x="151" y="0" width="3" height="35" fill="#000" />
                  </svg>
                  <div className="phleb-lbl-barcode-text font-mono">
                    *{printItem.barcodeNo || 'NO-BARCODE'}*
                  </div>
                </div>

                <div className="phleb-label-test-info">
                  <span className="phleb-lbl-tname">{printItem.itemName}</span>
                  <span className="phleb-lbl-specimen">
                    {printItem.sampleTypeName} / {printItem.containerName}
                  </span>
                </div>
              </div>
            </div>

            <div className="phleb-modal-footer">
              <button
                type="button"
                className="phleb-btn phleb-btn-secondary"
                onClick={() => setIsPrintModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="phleb-btn phleb-btn-primary"
                onClick={() => window.print()}
              >
                🖨️ Print Tube Label
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {isRejectModalOpen && rejectTarget && (
        <div className="phleb-modal-overlay" onClick={() => setIsRejectModalOpen(false)}>
          <div className="phleb-reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="phleb-reject-modal-header">
              <div>
                <span className="phleb-reject-modal-icon">⛔</span>
                <span className="phleb-reject-modal-title">Reject Specimen at Collection</span>
              </div>
              <button
                type="button"
                className="phleb-modal-close"
                onClick={() => setIsRejectModalOpen(false)}
              >✕</button>
            </div>

            <div className="phleb-reject-modal-body">
              {/* Patient Info Banner */}
              <div className="phleb-reject-patient-banner">
                <div className="phleb-reject-banner-row">
                  <span className="phleb-reject-banner-label">Patient</span>
                  <span className="phleb-reject-banner-value">{rejectTarget.patientName}</span>
                </div>
                <div className="phleb-reject-banner-row">
                  <span className="phleb-reject-banner-label">Investigation</span>
                  <span className="phleb-reject-banner-value">{rejectTarget.itemName}</span>
                </div>
                <div className="phleb-reject-banner-row">
                  <span className="phleb-reject-banner-label">Lab No</span>
                  <span className="phleb-reject-banner-value font-mono">{rejectTarget.ledgerTransactionNo}</span>
                </div>
                <div className="phleb-reject-banner-row">
                  <span className="phleb-reject-banner-label">Specimen</span>
                  <span className="phleb-reject-banner-value">{rejectTarget.sampleTypeName || 'Standard'}</span>
                </div>
              </div>

              {/* Warning */}
              <div className="phleb-reject-warning-box">
                <span>⚠️</span>
                <p>This action marks the specimen as <strong>Rejected at Collection Stage</strong>. The test will require re-collection. This action is audited and cannot be undone.</p>
              </div>

              {/* Rejection Reason */}
              <div className="phleb-form-group">
                <label className="phleb-form-label">Rejection Reason <span style={{ color: '#ef4444' }}>*</span></label>
                <select
                  className="phleb-select"
                  value={selectedReasonId}
                  onChange={(e) => setSelectedReasonId(e.target.value)}
                >
                  {rejectionReasons.map((r) => (
                    <option key={r.rejectionId} value={r.rejectionId.toString()}>
                      {r.rejectionReason}
                    </option>
                  ))}
                  <option value="CUSTOM">Other (specify below)</option>
                </select>
              </div>

              {/* Custom Reason */}
              {selectedReasonId === 'CUSTOM' && (
                <div className="phleb-form-group">
                  <label className="phleb-form-label">Specify Reason <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className="phleb-input"
                    placeholder="e.g. Patient refused, incorrect tube, haemolysed..."
                    value={customReasonText}
                    onChange={(e) => setCustomReasonText(e.target.value)}
                    maxLength={200}
                  />
                </div>
              )}

              {/* Remarks */}
              <div className="phleb-form-group">
                <label className="phleb-form-label">Additional Remarks <span style={{ color: '#94a3b8' }}>(optional)</span></label>
                <textarea
                  className="phleb-textarea"
                  rows={3}
                  placeholder="Any additional notes for the lab team..."
                  value={rejectRemarks}
                  onChange={(e) => setRejectRemarks(e.target.value)}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="phleb-reject-modal-footer">
              <button
                type="button"
                className="phleb-btn phleb-btn-secondary"
                onClick={() => setIsRejectModalOpen(false)}
                disabled={submittingReject}
              >
                Cancel
              </button>
              <button
                type="button"
                className="phleb-btn phleb-btn-danger"
                onClick={handleConfirmReject}
                disabled={submittingReject}
              >
                {submittingReject ? '⏳ Rejecting...' : '⛔ Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient & Order 360° Professional View-Only Modal */}
      <PatientOrder360Modal
        isOpen={Boolean(selectedPatientModal)}
        onClose={() => setSelectedPatientModal(null)}
        patientData={selectedPatientModal}
        onPrintBarcode={handleOpenPrintBarcode}
      />

      {/* Zero-Config Master-Driven Thermal Barcode Label Print Modal */}
      <ThermalBarcodePrintModal
        isOpen={isPrintModalOpen}
        onClose={() => {
          setIsPrintModalOpen(false);
          setPrintItem(null);
        }}
        sample={printItem}
        onPrintSuccess={() => {
          if (lastCollectedCount > 0) setLabelsPrinted(true);
          refreshAll();
        }}
      />
    </div>
  );
};

export default SampleCollectionPage;
